/**
 * Safely gets an item from localStorage with error handling
 */
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error(`Error reading ${key} from localStorage:`, e);
    return null;
  }
};

/**
 * Safely sets an item in localStorage with error handling
 */
const safeSetItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.error(`Error setting ${key} in localStorage:`, e);
    return false;
  }
};

// Import localforage to access Redux persist data
import localforage from "localforage";

import { APP_VERSION_STORAGE_KEY } from "@/constants/appVersionStorage";

// Keys for localStorage related to the app version
const VERSION_CHECK_TIMESTAMP = "rivet_version_check_timestamp";
const HAS_NEW_VERSION_KEY = "rivet_has_new_version";
const IS_REQUIRED_UPDATE_KEY = "rivet_is_required_update";

// Keys for auth-related data in localStorage/Redux
const AUTH_PERSIST_KEY = "persist:auth";
const ROOT_PERSIST_KEY = "persist:root";
const PROFILE_PERSIST_KEY = "persist:profile";
const FEATURES_PERSIST_KEY = "persist:features";
const PROJECT_PERSIST_KEY = "persist:project";
const RIVETAI_PERSIST_KEY = "persist:RivetAI"; // Key used by localforage in persistConfig

/**
 * Checks if a key exists in localforage
 */
const checkLocalForage = async (key: string): Promise<boolean> => {
  try {
    const value = await localforage.getItem(key);
    return value !== null;
  } catch (e) {
    console.error(`Error checking ${key} in localforage:`, e);
    return false;
  }
};

/**
 * Gets a value from localforage
 */
const getFromLocalForage = async (key: string): Promise<any> => {
  try {
    return await localforage.getItem(key);
  } catch (e) {
    console.error(`Error getting ${key} from localforage:`, e);
    return null;
  }
};

/**
 * Calculates the size of a JavaScript object in KB
 */
const calculateObjectSize = (obj: any): number => {
  try {
    const jsonString = JSON.stringify(obj);
    return jsonString.length / 1024; // Size in KB
  } catch (e) {
    console.error("Error calculating object size:", e);
    return 0;
  }
};

/**
 * Format a file size to be human-readable (KB/MB)
 */
const _formatFileSize = (sizeInKB: number): string => {
  if (sizeInKB < 1) {
    return `${Math.round(sizeInKB * 1024)} bytes`;
  }
  if (sizeInKB >= 1024) {
    return `${(sizeInKB / 1024).toFixed(2)} MB`;
  }
  return `${sizeInKB.toFixed(2)} KB`;
};

/**
 * Extracts authentication data from Redux persist storage
 * Now checks both localStorage and localforage
 */
const extractAuthData = async (): Promise<Record<string, any>> => {
  try {
    // First try localStorage (for backwards compatibility)
    const persistKey = safeGetItem(ROOT_PERSIST_KEY) ? ROOT_PERSIST_KEY : AUTH_PERSIST_KEY;
    const persistData = safeGetItem(persistKey);

    if (persistData) {
      const parsedData = JSON.parse(persistData);

      // If we have direct auth data from localStorage
      if (persistKey === AUTH_PERSIST_KEY) {
        return {
          [AUTH_PERSIST_KEY]: persistData,
          source: "localStorage",
        };
      }

      // If auth is nested in root from localStorage
      if (parsedData.auth) {
        return {
          [ROOT_PERSIST_KEY]: persistData,
          source: "localStorage",
        };
      }
    }

    // If we didn't find auth in localStorage, check localforage
    const persistRaw = await getFromLocalForage(RIVETAI_PERSIST_KEY);

    if (persistRaw) {
      try {
        // For localforage, data is stored as a string with stringified JSON
        const parsedPersist = typeof persistRaw === "string" ? JSON.parse(persistRaw) : persistRaw;

        // Check if auth exists in the parsed data
        if (parsedPersist && (parsedPersist.auth || parsedPersist._state?.auth)) {
          return {
            [RIVETAI_PERSIST_KEY]: persistRaw,
            source: "localforage",
          };
        }
      } catch (e) {
        console.error("Error parsing RivetAI persist data from localforage:", e);
      }
    }

    // Finally, check if we can get auth directly from Redux store
    if (typeof window !== "undefined" && window.__REDUX_STORE__) {
      const storeState = window.__REDUX_STORE__.getState();
      if (storeState && storeState.auth) {
        return {
          reduxStore: true,
          source: "reduxStore",
        };
      }
    }

    return {};
  } catch (error) {
    console.error("Error extracting auth data:", error);
    return {};
  }
};

/**
 * Get Redux state for specified slices - check both localStorage and localforage
 * Now with enhanced data size reporting
 */
const getReduxState = async (): Promise<Record<string, any>> => {
  try {
    const reduxState: Record<string, any> = {};
    const sizeInfo: Record<string, number> = {};

    // Define field size analysis type
    interface FieldSizeInfo {
      field: string;
      size: number;
    }

    // Try to access Redux state directly if possible through the global store
    if (typeof window !== "undefined" && window.__REDUX_STORE__) {
      try {
        const storeState = window.__REDUX_STORE__.getState();

        // Analyze all slices in the Redux store
        const storeSlices = Object.keys(storeState);
        const storeDetails = {} as Record<string, any>;
        let totalStoreSize = 0;

        // Calculate size and extract key information for each slice
        storeSlices.forEach((sliceName) => {
          const sliceData = storeState[sliceName];
          const sliceSize = calculateObjectSize(sliceData);
          totalStoreSize += sliceSize;

          // Deep analyze large slices to find the largest fields
          let largestFields: FieldSizeInfo[] = [];
          if (sliceSize > 50) {
            // Only analyze slices larger than 50KB
            try {
              const fields = Object.keys(sliceData || {});
              largestFields = fields
                .map((field) => {
                  const fieldData = sliceData[field];
                  const fieldSize = calculateObjectSize(fieldData);
                  return { field, size: fieldSize };
                })
                .filter((item) => item.size > 5) // Only fields larger than 5KB
                .sort((a, b) => b.size - a.size)
                .slice(0, 5); // Top 5 largest fields
            } catch (e) {
              // Ignore errors in field analysis
            }
          }

          // Add slice details
          storeDetails[sliceName] = {
            exists: true,
            sizeKB: Math.round(sliceSize * 100) / 100,
            keyCount: Object.keys(sliceData || {}).length,
            topLevelKeys: Object.keys(sliceData || {}).slice(0, 10), // Show first 10 keys
            largestFields: largestFields.length > 0 ? largestFields : undefined,
            ...(sliceName === "auth" && { loggedIn: !!sliceData?.loggedIn }),
          };
        });

        // Add total size information
        sizeInfo.totalReduxStoreKB = Math.round(totalStoreSize * 100) / 100;

        reduxState.actualState = {
          slices: storeSlices,
          totalSlices: storeSlices.length,
          totalSizeKB: Math.round(totalStoreSize * 100) / 100,
          hasAuth: !!storeState.auth,
          hasProfile: !!storeState.profile,
          hasFeatures: !!storeState.features,
          hasProject: !!storeState.project,
          authLoggedIn: storeState.auth?.loggedIn,
          details: storeDetails,
        };
      } catch (e) {
        reduxState.actualState = { error: "Failed to access Redux store" };
      }
    }

    // Check localforage for the main persist key
    try {
      const persistKeyPresent = await checkLocalForage(RIVETAI_PERSIST_KEY);
      if (persistKeyPresent) {
        const persistRaw = await getFromLocalForage(RIVETAI_PERSIST_KEY);
        let dataSize = 0;
        let dataStructure = {};

        if (persistRaw) {
          dataSize = calculateObjectSize(persistRaw);
          sizeInfo.localforageKB = Math.round(dataSize * 100) / 100;

          // Try to parse the structure (if it's a string or already an object)
          try {
            const parsedData = typeof persistRaw === "string" ? JSON.parse(persistRaw) : persistRaw;

            // If it's the Redux state structure, analyze it
            if (parsedData._persist || parsedData._state) {
              const stateData = parsedData._state || parsedData;
              dataStructure = {
                format: parsedData._persist ? "Redux Persist" : "Unknown",
                version: parsedData._persist?.version || "unknown",
                slices: Object.keys(stateData),
                totalSizeKB: Math.round(dataSize * 100) / 100,
              };
            } else {
              dataStructure = {
                format: "Unknown",
                keys: Object.keys(parsedData).slice(0, 10),
                totalSizeKB: Math.round(dataSize * 100) / 100,
              };
            }
          } catch (e) {
            dataStructure = { error: "Failed to parse localforage data" };
          }
        }

        reduxState.localforage = {
          persistKeyPresent: true,
          key: RIVETAI_PERSIST_KEY,
          sizeKB: Math.round(dataSize * 100) / 100,
          structure: dataStructure,
        };
      }
    } catch (e) {
      reduxState.localforage = { error: "Failed to access localforage data" };
    }

    // Check for root state which contains all slices in localStorage
    const rootData = safeGetItem(ROOT_PERSIST_KEY);
    if (rootData) {
      try {
        const rootSize = rootData.length / 1024;
        sizeInfo.rootKB = Math.round(rootSize * 100) / 100;

        const parsedRoot = JSON.parse(rootData);
        reduxState.root = {
          keys: Object.keys(parsedRoot),
          keyCount: Object.keys(parsedRoot).length,
          sizeKB: Math.round(rootSize * 100) / 100,
          hasAuth: !!parsedRoot.auth,
          hasProfile: !!parsedRoot.profile,
          hasFeatures: !!parsedRoot.features,
          hasProject: !!parsedRoot.project,
        };
      } catch (e) {
        reduxState.root = { error: "Failed to parse root state" };
      }
    }

    // Check individual slices if they're stored separately in localStorage
    const slicesToCheck = [
      { key: AUTH_PERSIST_KEY, name: "auth" },
      { key: PROFILE_PERSIST_KEY, name: "profile" },
      { key: FEATURES_PERSIST_KEY, name: "features" },
      { key: PROJECT_PERSIST_KEY, name: "project" },
    ];

    for (const { key, name } of slicesToCheck) {
      const data = safeGetItem(key);
      if (data) {
        try {
          const sliceSize = data.length / 1024;
          sizeInfo[`${name}KB`] = Math.round(sliceSize * 100) / 100;

          const parsed = JSON.parse(data);
          reduxState[name] = {
            exists: true,
            keys: Object.keys(parsed),
            keyCount: Object.keys(parsed).length,
            sizeKB: Math.round(sliceSize * 100) / 100,
            source: "localStorage",
          };
        } catch (e) {
          reduxState[name] = { exists: true, error: "Failed to parse", source: "localStorage" };
        }
      } else {
        // Even if not in localStorage, check if it might exist in actual Redux state
        const existsInReduxState =
          reduxState.actualState &&
          reduxState.actualState.details &&
          reduxState.actualState.details[name];

        if (existsInReduxState) {
          reduxState[name] = {
            exists: true,
            source: "reduxStore",
            sizeKB: reduxState.actualState.details[name].sizeKB,
            keyCount: reduxState.actualState.details[name].keyCount,
            note: "Found in Redux store",
          };
        } else {
          reduxState[name] = {
            exists: false,
            source: "none",
            note: "Not found in localStorage or Redux store",
          };
        }
      }
    }

    // Add aggregate size information
    reduxState.sizeInfo = sizeInfo;

    return reduxState;
  } catch (error) {
    console.error("Error getting Redux state:", error);
    return { error: "Failed to get Redux state" };
  }
};

/**
 * Clear React Query Cache
 * This should be called before page reload to ensure fresh data on next load
 */
const clearReactQueryCache = (): void => {
  try {
    // Find the React Query client if it exists in the window object
    // This is a common pattern in React Query v4+
    if (window.__REACT_QUERY_GLOBAL_CLIENT) {
      window.__REACT_QUERY_GLOBAL_CLIENT.clear();
      return;
    }

    // Alternative approach: set a flag in localStorage to invalidate cache on next load
    safeSetItem("react_query_cache_bust", Date.now().toString());
  } catch (error) {
    // Error clearing React Query cache
  }
};

/**
 * Debugging function to check React Query cache state
 * @returns Object containing cache information or null if not available
 */
export const debugReactQueryCache = (): {
  queryCache: any;
  isCacheEmpty: boolean;
  cacheSize: number;
} | null => {
  try {
    if (window.__REACT_QUERY_GLOBAL_CLIENT) {
      // Get the queries from the query cache if possible
      const queryCache = window.__REACT_QUERY_GLOBAL_CLIENT.getQueryCache?.() || {};
      const queries = queryCache.queries || [];

      // Calculate total cache size by serializing each query individually
      // This provides a more accurate size than serializing the entire collection
      let totalCacheSize = 0;
      const queriesWithSize = queries.map((q: any) => {
        try {
          // Include queryKey and state.data which are the main memory consumers
          const queryData = {
            queryKey: q.queryKey,
            data: q.state?.data,
            dataUpdatedAt: q.state?.dataUpdatedAt,
          };
          const serialized = JSON.stringify(queryData);
          const size = serialized.length / 1024; // Size in KB
          totalCacheSize += size;

          return {
            queryKey: q.queryKey,
            sizeKB: size.toFixed(2),
            lastUpdated: new Date(q.state?.dataUpdatedAt || Date.now()).toISOString(),
          };
        } catch (e) {
          return {
            queryKey: q.queryKey,
            sizeKB: 0,
            error: "Could not calculate size",
          };
        }
      });

      return {
        queryCache: {
          queryCount: queries.length,
          queries: queriesWithSize,
          totalSizeKB: totalCacheSize,
        },
        isCacheEmpty: queries.length === 0,
        cacheSize: totalCacheSize,
      };
    }

    return null;
  } catch (error) {
    console.error("Error debugging React Query cache:", error);
    return null;
  }
};

/**
 * Debugging function to check Redux persist state
 * @returns Promise that resolves to an object with information about Redux persist state
 */
export const debugReduxPersistState = async (): Promise<{
  hasAuth: boolean;
  persistKeys: string[];
  authData: Record<string, any>;
  reduxState: Record<string, any>;
  localforageData: boolean;
}> => {
  try {
    // Check for persist:keys
    const persistKeys = [];
    const allStorageKeys = Object.keys(localStorage);

    // Look for Redux persist keys with a more flexible approach
    for (const key of allStorageKeys) {
      if (key.startsWith("persist:")) {
        persistKeys.push(key);
      }
    }

    // Check localforage for RivetAI data
    const hasLocalforageData = await checkLocalForage(RIVETAI_PERSIST_KEY);

    // Extract auth data with more reliable detection
    const extractedAuth = await extractAuthData();
    const reduxState = await getReduxState();

    // Get auth state from redux store directly
    const authExistsInStore =
      reduxState.actualState &&
      reduxState.actualState.hasAuth &&
      reduxState.actualState.authLoggedIn;

    // Check auth slice
    const authSliceExists = reduxState.auth && reduxState.auth.exists;

    // Check if auth is in localforage
    const authInLocalForage =
      extractedAuth.source === "localforage" ||
      (reduxState.localforage && reduxState.localforage.persistKeyPresent);

    return {
      // Consider auth exists if it exists in any of our checks
      hasAuth: authExistsInStore || authSliceExists || authInLocalForage || persistKeys.length > 0,
      persistKeys,
      authData: extractedAuth,
      reduxState,
      localforageData: hasLocalforageData,
    };
  } catch (error) {
    console.error("Error debugging Redux persist state:", error);
    return {
      hasAuth: false,
      persistKeys: [],
      authData: {},
      reduxState: {},
      localforageData: false,
    };
  }
};

/**
 * Clears the application cache while preserving authentication data
 */
export const clearAppCache = async (): Promise<void> => {
  console.group("%c🧹 App Cache Clearing Process", "font-weight: bold; color: #2196F3;");

  try {
    // Log the state before clearing
    console.group("📊 State Before Clearing");

    Object.keys(localStorage);
    // Debug information available in development
    console.groupEnd();

    // 1. Extract auth data before clearing cache
    const authData = await extractAuthData();
    // Extracted auth data for preservation

    // 2. Remember the version info
    const versionData = {
      [APP_VERSION_STORAGE_KEY]: safeGetItem(APP_VERSION_STORAGE_KEY),
      [VERSION_CHECK_TIMESTAMP]: safeGetItem(VERSION_CHECK_TIMESTAMP),
      [HAS_NEW_VERSION_KEY]: safeGetItem(HAS_NEW_VERSION_KEY),
      [IS_REQUIRED_UPDATE_KEY]: safeGetItem(IS_REQUIRED_UPDATE_KEY),
    };
    // Preserved version data

    // 3. Clear React Query cache
    clearReactQueryCache();

    // 4. Clear localStorage
    localStorage.clear();

    // 5. Restore auth data
    Object.entries(authData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        safeSetItem(key, value);
      }
    });

    // 6. Restore version data
    Object.entries(versionData).forEach(([key, value]) => {
      if (value !== null) {
        safeSetItem(key, value);
      }
    });

    // 7. Clear sessionStorage
    sessionStorage.clear();

    // 8. Clear browser caches if available
    if ("caches" in window) {
      try {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((key) => caches.delete(key)));
      } catch (e) {
        // Error clearing cache storage
      }
    }

    // App cache cleared successfully while preserving authentication data
  } catch (error) {
    // Failed to clear app cache
    // Don't throw error here to prevent page reload issues
  } finally {
    console.groupEnd();
  }
};

/**
 * Test function to verify cache clearing would work correctly
 * This does a dry run without actually clearing anything
 * @param verbose When true, logs detailed information (default: false)
 */
export const testCacheClearing = async (verbose: boolean = false): Promise<void> => {
  console.group("%c🔍 Cache Clearing Test", "font-weight: bold; color: #4CAF50;");

  try {
    // Get Redux state information
    const reduxState = await debugReduxPersistState();
    const sizeInfo = reduxState.reduxState.sizeInfo as Record<string, number>;
    const reduxDetails = reduxState.reduxState.actualState?.details || {};

    // Calculate sizes
    const reduxStoreSize = sizeInfo?.totalReduxStoreKB || 0;
    const localforageSize = sizeInfo?.localforageKB || 0;

    // Find large Redux slices (> 100KB)
    Object.entries(reduxDetails)
      .filter(([_, data]) => (data as any).sizeKB > 100)
      .map(([name, data]) => ({
        name,
        size: (data as any).sizeKB,
        largestFields: (data as any).largestFields || [],
      }))
      .sort((a, b) => b.size - a.size);

    // Get localStorage information
    const localStorageKeys = Object.keys(localStorage);
    let totalLocalStorageSize = 0;

    localStorageKeys.forEach((key) => {
      const item = localStorage.getItem(key);
      if (item) {
        totalLocalStorageSize += item.length / 1024;
      }
    });

    // Get React Query cache size estimation
    const queryCache = debugReactQueryCache();
    let reactQuerySize = 0;

    if (queryCache && !queryCache.isCacheEmpty) {
      // Use the directly calculated size from queryCache
      reactQuerySize = queryCache.cacheSize;
    }

    // Authentication data check
    const authData = await extractAuthData();
    Object.keys(authData).length > 0;
    authData.source || "none";

    // Calculate total application storage
    totalLocalStorageSize + localforageSize + reduxStoreSize + reactQuerySize;

    // Display simplified summary intended for QA testers
    console.group("%c💾 CACHE SIZE SUMMARY", "font-weight: bold; color: #E91E63; font-size: 14px;");

    // Storage usage information available in development

    console.groupEnd(); // End summary

    // Detailed information available in development
    if (verbose) {
      if (sizeInfo) {
        // Redux slice sizes available in development
        // Detailed analysis available in development
      }
      console.groupEnd();

      // React Query Details available in development
      // LocalStorage Details available in development
    }

    // Cache analysis completed
  } catch (error) {
    // Error during cache clearing test
  } finally {
    console.groupEnd();
  }
};

// TypeScript declaration for global React Query client
declare global {
  interface Window {
    __REACT_QUERY_GLOBAL_CLIENT?: {
      clear: () => void;
      getQueryCache?: () => any;
    };
    __REDUX_STORE__?: {
      getState: () => any;
      dispatch: (action: any) => any;
    };
  }
}
